import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  FileText, 
  Clock, 
  CheckCircle2, 
  XCircle, 
  ArrowRight,
  AlertCircle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SMEDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [pendingInterventions, setPendingInterventions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, interventionsRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/interventions`)
      ]);
      setStats(statsRes.data);
      setPendingInterventions(interventionsRes.data.filter(i => i.status === 'pending').slice(0, 5));
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin h-8 w-8 border-4 border-slate-900 border-t-transparent rounded-full"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8 fade-in" data-testid="sme-dashboard">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">Tableau de bord</h1>
          <p className="text-slate-500 mt-1">Bienvenue, {user?.name}</p>
          {user?.company_name && (
            <p className="text-sm text-slate-600 mt-1">Entreprise : {user.company_name}</p>
          )}
        </div>

        {/* Alert for pending validations */}
        {stats?.pending_validations > 0 && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-orange-500" />
            <div className="flex-1">
              <p className="text-orange-800 font-medium">
                Vous avez {stats.pending_validations} intervention(s) en attente de validation
              </p>
              <p className="text-orange-600 text-sm mt-1">
                Veuillez examiner et donner votre avis sur ces interventions
              </p>
            </div>
            <Button asChild variant="outline" className="border-orange-300 text-orange-700 hover:bg-orange-100">
              <Link to="/pme/pending" data-testid="review-pending-btn">
                Examiner maintenant
              </Link>
            </Button>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="border-slate-200 shadow-sm" data-testid="stat-total">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 font-medium uppercase tracking-wider">Total</p>
                  <p className="text-3xl font-bold text-slate-900 mt-1">{stats?.total_interventions || 0}</p>
                </div>
                <div className="p-3 bg-slate-100 rounded-lg">
                  <FileText className="w-6 h-6 text-slate-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200 shadow-sm" data-testid="stat-pending">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-orange-600 font-medium uppercase tracking-wider">En attente</p>
                  <p className="text-3xl font-bold text-slate-900 mt-1">{stats?.pending_validations || 0}</p>
                </div>
                <div className="p-3 bg-orange-100 rounded-lg">
                  <Clock className="w-6 h-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200 shadow-sm" data-testid="stat-validated">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-emerald-600 font-medium uppercase tracking-wider">Validées</p>
                  <p className="text-3xl font-bold text-slate-900 mt-1">{stats?.validated || 0}</p>
                </div>
                <div className="p-3 bg-emerald-100 rounded-lg">
                  <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200 shadow-sm" data-testid="stat-rejected">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-red-600 font-medium uppercase tracking-wider">Rejetées</p>
                  <p className="text-3xl font-bold text-slate-900 mt-1">{stats?.rejected || 0}</p>
                </div>
                <div className="p-3 bg-red-100 rounded-lg">
                  <XCircle className="w-6 h-6 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Pending Validations */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between pb-4">
            <div>
              <CardTitle className="text-xl font-semibold text-slate-900">Validations en attente</CardTitle>
              <CardDescription className="text-slate-500">Interventions nécessitant votre avis</CardDescription>
            </div>
            <Button variant="ghost" asChild className="text-slate-600 hover:text-slate-900">
              <Link to="/pme/pending" data-testid="view-all-pending">
                Voir tout <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            </Button>
          </CardHeader>
          <CardContent>
            {pendingInterventions.length === 0 ? (
              <div className="text-center py-12">
                <CheckCircle2 className="w-12 h-12 text-emerald-300 mx-auto mb-4" />
                <p className="text-slate-500">Tout est à jour ! Aucune validation en attente.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {pendingInterventions.map((intervention) => (
                  <Link 
                    key={intervention.id} 
                    to={`/pme/intervention/${intervention.id}`}
                    className="block"
                    data-testid={`pending-${intervention.id}`}
                  >
                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3">
                          <p className="font-medium text-slate-900 truncate">{intervention.intervention_type}</p>
                          <Badge variant="outline" className="bg-orange-100 text-orange-700 border-orange-200">
                            En attente
                          </Badge>
                        </div>
                        <p className="text-sm text-slate-500 mt-1">
                          Par {intervention.consultant_name} • {intervention.date} • {intervention.duration_hours}h
                        </p>
                      </div>
                      <ArrowRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};
